import { calculatePayout } from '@homestake/shared/utils';
import { observer, use$ } from '@legendapp/state/react';
import {
  Box,
  Card,
  type CardProps,
  Divider,
  Group,
  Stack,
  Text,
} from '@mantine/core';
import { CaretDownIcon } from '@phosphor-icons/react';
import Decimal from 'decimal.js';
import { useTranslation } from 'react-i18next';
import {
  configurationState$,
  gameState$,
  manualBetState$,
  towersState$,
} from '../../../state';
import { calculateMultiplier } from '../../../utils/towers.utils';

// Helper function to format amount with proper sign and color
const formatAmount = ({
  amount,
  decimalScale,
  showSign = true,
}: {
  amount: number;
  showSign?: boolean;
  decimalScale: number;
}): { value: string; color: string } => {
  const absAmount = new Decimal(amount).abs();
  const formattedValue = absAmount.toFixed(decimalScale, Decimal.ROUND_DOWN);

  if (amount === 0) {
    return { value: '0.00', color: 'dimmed' };
  }

  if (amount > 0) {
    return {
      value: showSign ? `+${formattedValue}` : formattedValue,
      color: 'green',
    };
  }

  return {
    value: `-${formattedValue}`,
    color: 'red',
  };
};

// Reusable component for profit sections
const ProfitSection = observer(
  ({
    label,
    amount,
    multiplier,
    dataTestId,
    decimalScale,
  }: {
    label: string;
    amount: number;
    multiplier: number;
    dataTestId: string;
    decimalScale: number;
  }) => {
    const { value: amountText, color: amountColor } = formatAmount({
      amount,
      decimalScale,
    });
    const { t } = useTranslation();
    return (
      <Stack data-test-id={dataTestId} gap={12}>
        <Group align="center" justify="space-between">
          <Text c="dimmed" data-test-id={`${dataTestId}-label`} size="sm">
            {label}
          </Text>
          <Text
            c={amountColor}
            data-test-id={`${dataTestId}-amount`}
            fw={600}
            size="sm"
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {amountText}
          </Text>
        </Group>
        <Group align="center" justify="space-between">
          <Text
            c="dimmed"
            data-test-id={`${dataTestId}-multiplier-label`}
            size="sm"
          >
            {t('manualBetProfitCard.multiplier')}
          </Text>
          <Text
            c="dimmed"
            data-test-id={`${dataTestId}-multiplier`}
            size="sm"
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {new Decimal(multiplier).toFixed(2, Decimal.ROUND_DOWN)}x
          </Text>
        </Group>
      </Stack>
    );
  }
);

// Divider with arrow component
const DividerWithArrow = () => (
  <Box data-test-id="profit-divider-container" pos="relative">
    <Divider color="dark.4" data-test-id="profit-divider" size={1} />
    <Box
      data-test-id="profit-divider-arrow"
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: 'var(--mantine-color-dark-7)',
        padding: '4px',
        borderRadius: '50%',
      }}
    >
      <CaretDownIcon color="var(--mantine-color-dimmed)" size={12} />
    </Box>
  </Box>
);

export type ManualBetProfitCardProps = CardProps;

export const ManualBetProfitCard = observer(
  (props: ManualBetProfitCardProps) => {
    const betAmount = use$(manualBetState$.betAmount);
    const isBetting = use$(manualBetState$.isBetting);
    const bricks = use$(towersState$.bricks);
    const pattern = use$(gameState$.pattern);
    const decimalScale = use$(configurationState$.decimalScale);
    const { t } = useTranslation();

    if (!isBetting) {
      return null;
    }

    // Calculate current state
    const pickedBricksCount = bricks.length;

    // Current profit calculation
    let currentProfit: Decimal;
    let currentMultiplier = 0;

    if (!isBetting) {
      // Not betting - no profit/loss
      currentProfit = new Decimal(0);
      currentMultiplier = 0;
    } else if (pickedBricksCount === 0) {
      // Just started betting, no bricks picked yet - current profit is negative bet amount
      currentProfit = new Decimal(betAmount).neg();
      currentMultiplier = 0;
    } else {
      // Calculate profit based on current picks
      currentMultiplier = calculateMultiplier(pickedBricksCount, pattern);

      // Use utility function for consistent calculation
      const { profit } = calculatePayout({
        betAmount: new Decimal(betAmount),
        multiplier: new Decimal(currentMultiplier),
        withRoundedMultiplier: true,
      });

      currentProfit = profit;
    }

    // Profit on next brick calculation
    const nextBrickIndex = pickedBricksCount + 1;
    const nextMultiplier = isBetting
      ? calculateMultiplier(nextBrickIndex, pattern)
      : 0;

    let nextBrickProfit: Decimal;
    if (isBetting) {
      // Use utility function for consistent calculation
      const { profit } = calculatePayout({
        betAmount: new Decimal(betAmount),
        multiplier: new Decimal(nextMultiplier),
        withRoundedMultiplier: true,
      });

      nextBrickProfit = profit;
    } else {
      nextBrickProfit = new Decimal(0);
    }

    return (
      <Card
        bd="2px solid dark.6"
        bg="transparent"
        data-test-id="manual-bet-profit-card"
        p={16}
        withBorder
        {...props}
      >
        <Stack gap={20}>
          <ProfitSection
            amount={currentProfit.toNumber()}
            dataTestId="current-profit"
            decimalScale={decimalScale}
            label={t('manualBetProfitCard.currentProfit')}
            multiplier={currentMultiplier}
          />

          <DividerWithArrow />

          <ProfitSection
            amount={nextBrickProfit.toNumber()}
            dataTestId="next-brick-profit"
            decimalScale={decimalScale}
            label={t('manualBetProfitCard.profitOnNextBrick')}
            multiplier={nextMultiplier}
          />
        </Stack>
      </Card>
    );
  }
);
